import {
	type DeliveryMethodKind,
	type GetVendorsFilteredInput,
	resolveDeliveryMethodKinds,
} from "@dransay/api/models";

const FILTERED_LIST_PAGE_SIZE = 1000;

export type VendorsFilteredListLocation = {
	postalCode?: string | null;
	streetName?: string | null;
	houseNr?: string | null;
	city?: string | null;
	streetAdditional?: string | null;
};

export type VendorsFilteredSortBy = "name" | "freeFrom" | "distance";

export type BuildVendorsFilteredListInputArgs = {
	deliveryMethodKind?: DeliveryMethodKind | null;
	/**
	 * When false, request only top pharmacies (`isTop: true`).
	 * Defaults to true (include all) when omitted — map radius fetch always wants the full page.
	 */
	listIncludeNonTop?: boolean;
	/** Inclusive search radius in meters. Only applied alongside a resolved location. */
	maxDistanceMeters?: number;
	sortBy?: VendorsFilteredSortBy;
	sortOrder?: "asc" | "desc";
	/** Narrow a courier browse to express pharmacies that are open within 10 km. */
	expressCourierOnly?: boolean;
} & VendorsFilteredListLocation;

const optionalLocationField = (
	value: string | null | undefined,
): string | undefined => {
	const trimmed = value?.trim();
	return trimmed ? trimmed : undefined;
};

const buildLocation = (
	args: VendorsFilteredListLocation,
): GetVendorsFilteredInput["location"] => {
	const postalCode = optionalLocationField(args.postalCode);
	if (!postalCode) return undefined;
	const streetName = optionalLocationField(args.streetName);
	const houseNr = optionalLocationField(args.houseNr);
	const city = optionalLocationField(args.city);
	const streetAdditional = optionalLocationField(args.streetAdditional);
	return {
		postalCode,
		...(streetName ? { streetName } : {}),
		...(houseNr ? { houseNr } : {}),
		...(city ? { city } : {}),
		...(streetAdditional ? { streetAdditional } : {}),
	};
};

/**
 * Shared request body for POST /api/vendors-filtered (PLP list + pharmacy map).
 */
export const buildVendorsFilteredListInput = (
	args: BuildVendorsFilteredListInputArgs,
): GetVendorsFilteredInput => {
	const location = buildLocation(args);
	const deliveryMethodKinds = args.expressCourierOnly
		? (["courier", "express-courier"] as DeliveryMethodKind[])
		: resolveDeliveryMethodKinds(args.deliveryMethodKind, false);
	const listIncludeNonTop = args.listIncludeNonTop ?? true;
	const filter: NonNullable<GetVendorsFilteredInput["filter"]> = {};
	if (deliveryMethodKinds) {
		filter.deliveryMethodKinds = deliveryMethodKinds;
	}
	if (!listIncludeNonTop) {
		filter.isTop = true;
	}
	if (args.maxDistanceMeters != null && location != null) {
		filter.maxDistance = args.maxDistanceMeters;
	}
	const sortBy = args.sortBy;
	const sort =
		sortBy != null
			? {
					sortBy,
					sortOrder: args.sortOrder ?? ("asc" as const),
				}
			: undefined;
	return {
		page: 0,
		pageSize: FILTERED_LIST_PAGE_SIZE,
		...(Object.keys(filter).length > 0 ? { filter } : {}),
		...(location ? { location } : {}),
		...(sort ? { sort } : {}),
	};
};

/**
 * Map / pickup-courier page: distance sort when postal is set, all pharmacies, and
 * deliberately no `maxDistance` — the fixed 10/15/20/25 km options are bucketed
 * client-side from this single page, so a server cap would silently limit
 * "Jede Entfernung" / "Any radius" to that distance (MKP-2054, FR-022).
 */
export const buildVendorsFilteredMapInput = (
	args: {
		deliveryMethodKind?: DeliveryMethodKind | null;
		expressCourierOnly?: boolean;
	} & VendorsFilteredListLocation,
): GetVendorsFilteredInput => {
	const postalCode = optionalLocationField(args.postalCode);
	return buildVendorsFilteredListInput({
		...args,
		listIncludeNonTop: true,
		sortBy: postalCode ? "distance" : "name",
		sortOrder: "asc",
	});
};
